import { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID  = 'service_2n2ve0g';
const EMAILJS_TEMPLATE_ID = 'template_f977szg';
const EMAILJS_PUBLIC_KEY  = 'kYpSxUDBTnclm-UVq';

export default function NewsletterPopup() {
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [error, setError] = useState('');

  useEffect(() => {
    const subscribed = localStorage.getItem('cv_newsletter');
    if (!subscribed) {
      const timer = setTimeout(() => setVisible(true), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const close = () => setVisible(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) { setError('Please enter your name.'); return; }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Please enter a valid email.'); return; }

    setError('');
    setStatus('sending');

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          sub_name:  form.name,
          sub_email: form.email,
          sub_time:  new Date().toLocaleString(),
        },
        EMAILJS_PUBLIC_KEY
      );
      setStatus('success');
      localStorage.setItem('cv_newsletter', 'subscribed');
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[990] bg-black/60 dark:bg-white/60 backdrop-blur-sm"
        onClick={close}
      />

      {/* Popup */}
      <div className="fixed inset-0 z-[991] flex items-center justify-center px-4">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-fade-up">

          {/* Top decorative bar */}
          <div className="h-1 w-full bg-gradient-to-r from-white/20 via-white to-white/20" />

          {/* Close button */}
          <button
            onClick={close}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/50 hover:text-white transition-colors z-10"
            aria-label="Close newsletter popup"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>

          <div className="p-8 relative">
            {status === 'success' ? (
              /* Success State */
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-5">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-white">
                    <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10 16l4 4 8-8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-2xl text-white mb-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500,letterSpacing:'0.02em'}}>
                  YOU'RE IN!
                </h3>
                <p className="text-white/50 text-sm leading-relaxed mb-6">
                  Thanks for subscribing, <strong className="text-white">{form.name}</strong>! We'll send you the latest updates on web development, design, and digital marketing.
                </p>
                <button onClick={close} className="btn-primary-inv text-sm py-2.5 px-6">
                  Continue Browsing
                </button>
              </div>
            ) : (
              <>
                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center mb-5">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-white">
                    <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>

                {/* Heading */}
                <h3 className="text-2xl text-white mb-1" style={{fontFamily:'Anton,sans-serif',fontWeight:500,letterSpacing:'0.02em'}}>
                  STAY IN THE LOOP.
                </h3>
                <p className="text-white/50 text-sm leading-relaxed mb-6">
                  Get the latest tips on web development, design, SEO, and digital marketing — straight to your inbox. No spam, ever.
                </p>

                {/* Perks */}
                <div className="flex flex-col gap-2 mb-6">
                  {[
                    '🚀 Web & app development tips',
                    '📈 SEO & marketing insights',
                    '🎨 Design trends & resources',
                  ].map(item => (
                    <div key={item} className="flex items-center gap-2 text-xs text-white/40">
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={form.name}
                    onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setError(''); }}
                    className="w-full bg-white/5 dark:bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-white/40 transition-colors"
                    aria-label="Your name"
                  />
                  <input
                    type="email"
                    placeholder="Your Email Address"
                    value={form.email}
                    onChange={e => { setForm(f => ({ ...f, email: e.target.value })); setError(''); }}
                    className="w-full bg-white/5 dark:bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-white/40 transition-colors"
                    aria-label="Your email"
                  />

                  {error && <p className="text-red-400 text-xs">{error}</p>}
                  {status === 'error' && <p className="text-red-400 text-xs">Failed to subscribe. Please try again.</p>}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full bg-white dark:bg-[#111] text-black dark:text-white font-medium text-sm py-3 rounded-xl hover:bg-white/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {status === 'sending' ? (
                      <>
                        <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3"/>
                          <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                        Subscribing...
                      </>
                    ) : (
                      <>
                        Subscribe Now
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path d="M3 8H13M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </>
                    )}
                  </button>
                </form>

                <p className="text-center text-white/20 text-xs mt-4">
                  No spam. Unsubscribe anytime.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
